'use client';

import React, { useEffect, useMemo, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { getLogoBrand } from "@/utils/getLogoBrand";
import { cn } from "@/lib/utils";

interface BrandItem {
    codice: string;
    marca: string;
    count?: number;
}

interface BrandGridProps {
    className?: string;
    openDialog?: boolean;
    singleRow?: boolean;
    hideTitle?: boolean;
    initialBrands?: BrandItem[] | null;
    variant?: "grid" | "carousel";
    basePath?: string;
}

const DEFAULT_LIMIT = 12;
const API_BASE_URL = (process.env.NEXT_PUBLIC_API_URL || "").trim().replace(/\/$/, "");

function buildApiUrl(path: string) {
    return API_BASE_URL ? `${API_BASE_URL}${path}` : path;
}

export default function BrandGrid({
    className,
    openDialog = true,
    singleRow = false,
    hideTitle = false,
    initialBrands = [],
    variant = "grid",
    basePath = "/parco-veicoli"
}: BrandGridProps) {
    const normalizedInitialBrands = useMemo(
        () => (Array.isArray(initialBrands) ? initialBrands : []),
        [initialBrands]
    );
    const router = useRouter();
    const searchParams = useSearchParams();
    const [brands, setBrands] = useState<BrandItem[]>(normalizedInitialBrands);
    const [showAll, setShowAll] = useState(false);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [selectedBrand, setSelectedBrand] = useState<BrandItem | null>(null);
    const [models, setModels] = useState<{ modello: string; count?: number }[]>([]);
    const [loadingModels, setLoadingModels] = useState(false);
    const [selectedModel, setSelectedModel] = useState<string>("");

    useEffect(() => {
        if (normalizedInitialBrands.length > 0) {
            setBrands(normalizedInitialBrands);
            return;
        }

        const fetchBrands = async () => {
            try {
                const res = await fetch(buildApiUrl("/api/articoli/marca"));
                if (!res.ok) {
                    throw new Error(`Brand request failed with status ${res.status}`);
                }
                const data = await res.json();
                setBrands(Array.isArray(data?.data) ? data.data : []);
            } catch (error) {
                console.error("Errore nel recupero dei brand:", error);
            }
        };

        fetchBrands();
    }, [normalizedInitialBrands]);

    const currentOrder = searchParams?.get("order") || "";
    const catalogHref = currentOrder ? `${basePath}?order=${encodeURIComponent(currentOrder)}` : basePath;

    const sortedBrands = useMemo(
        () =>
            [...brands]
                .filter((item) => item?.codice)
                .sort((a, b) => (b.count || 0) - (a.count || 0)),
        [brands]
    );

    const visibleBrands = useMemo(() => {
        if (variant === "carousel") return sortedBrands;
        if (singleRow) {
            return showAll ? sortedBrands : sortedBrands.slice(0, 9);
        }
        return showAll ? sortedBrands : sortedBrands.slice(0, DEFAULT_LIMIT);
    }, [sortedBrands, showAll, singleRow, variant]);

    const handleBrandClick = async (brand: BrandItem) => {
        if (!openDialog) {
            const params = new URLSearchParams({
                tipoVeicolo: 'none',
                marca: brand.codice,
            });
            if (currentOrder) {
                params.set("order", currentOrder);
            }
            router.push(`${basePath}?${params.toString()}`);
            return;
        }

        setSelectedBrand(brand);
        setIsDialogOpen(true);
        setLoadingModels(true);
        setSelectedModel("");
        try {
            const res = await fetch(
                buildApiUrl(`/api/articoli/modello?marca=${encodeURIComponent(brand.codice)}`)
            );
            if (!res.ok) {
                throw new Error(`Model request failed with status ${res.status}`);
            }
            const data = await res.json();
            setModels(Array.isArray(data?.data) ? data.data : []);
        } catch (error) {
            console.error("Errore nel recupero dei modelli:", error);
            setModels([]);
        } finally {
            setLoadingModels(false);
        }
    };

    const handleNavigate = () => {
        const params = new URLSearchParams({
            tipoVeicolo: 'none',
            marca: selectedBrand?.codice || '',
            modello: selectedModel || ''
        });
        if (currentOrder) {
            params.set("order", currentOrder);
        }
        // clean empty
        for (const key of params.keys()) {
            if (!params.get(key)) params.delete(key);
        }
        router.push(`${basePath}?${params.toString()}`);
        setIsDialogOpen(false);
    };

    return (
        <section className={cn("max-w-6xl mx-auto px-4", className)}>
            {!hideTitle && (
                <h2 className="text-center text-primary font-bold text-2xl sm:text-3xl mb-6">
                    Scegli tra i nostri migliori Brand
                </h2>
            )}
            <div
                className={cn(
                    variant === "carousel"
                        ? "flex overflow-x-auto gap-3 pb-2 scrollbar-hide no-scrollbar"
                        : cn(
                            "grid gap-3 sm:gap-4",
                            singleRow
                                ? "grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-7 xl:grid-cols-9 w-full"
                                : "grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6"
                          ),
                    className
                )}
                style={variant === "carousel" ? { scrollbarWidth: 'none', msOverflowStyle: 'none' } : {}}
            >
                {visibleBrands.map((brand) => (
                    <button
                        key={brand.codice}
                        type="button"
                        onClick={() => handleBrandClick(brand)}
                        className={cn(
                            "bg-white border rounded-2xl shadow-sm flex items-center justify-center transition hover:-translate-y-1 hover:shadow-md shrink-0",
                            variant === "carousel" ? "w-24 h-24 p-2" : "p-4 sm:p-5",
                            selectedBrand?.codice === brand.codice ? "border-primary ring-2 ring-primary/30" : "border-gray-200"
                        )}
                        aria-label={`Apri i modelli di ${brand.marca || brand.codice}`}
                    >
                        <div className={cn(
                            "relative w-full",
                            variant === "carousel" ? "h-12" : "h-16 sm:h-20 max-w-[120px]"
                        )}>
                            <Image
                                src={getLogoBrand(brand.codice)}
                                alt={brand.marca || brand.codice}
                                fill
                                className="object-contain"
                                sizes={variant === "carousel" ? "96px" : "(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 120px"}
                                priority={false}
                            />
                        </div>
                    </button>
                ))}
            </div>

            {variant === "grid" && (sortedBrands.length > DEFAULT_LIMIT || singleRow) && (
                <div className="flex flex-col items-center gap-4 sm:gap-5 my-2">
                    {(singleRow ? sortedBrands.length > 9 : sortedBrands.length > DEFAULT_LIMIT) && (
                        <div className="w-full max-w-3xl">
                            <div className="relative mt-2 mb-2 flex items-center justify-center">
                                <div className="absolute w-full h-px bg-gray-400/50"></div>
                                <button
                                    type="button"
                                    onClick={() => setShowAll((prev) => !prev)}
                                    className="relative bg-white px-3 text-sm sm:text-base text-[#0f172a] font-medium tracking-wide hover:text-primary cursor-pointer transition-colors uppercase"
                                >
                                    {showAll ? "Mostra di meno" : "Mostra di più +"}
                                </button>
                            </div>
                        </div>
                    )}
                    {!singleRow && (
                        <>
                            <div className="text-sm text-gray-600">Oppure</div>
                            <Link
                                href={catalogHref}
                                className="bg-gradient-custom text-white font-semibold px-6 sm:px-10 py-3 rounded-lg shadow transition hover:translate-y-[-2px]"
                            >
                                Scopri il nostro vasto parco auto
                            </Link>
                        </>
                    )}
                </div>
            )}

            {isDialogOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center px-4 py-8 bg-black/50 backdrop-blur-sm">
                    <div className="relative w-full max-w-5xl bg-white rounded-3xl shadow-2xl p-5 sm:p-8">
                        <button
                            type="button"
                            onClick={() => setIsDialogOpen(false)}
                            className="absolute top-4 right-4 h-10 w-10 rounded-full border border-gray-300 text-gray-600 hover:border-primary hover:text-primary transition flex items-center justify-center"
                            aria-label="Chiudi"
                        >
                            ✕
                        </button>
                        <div className="flex items-center gap-4 sm:gap-6 mb-6 sm:mb-8">
                            <div className="relative h-14 w-20 sm:h-16 sm:w-24">
                                {selectedBrand && (
                                    <Image
                                        src={getLogoBrand(selectedBrand.codice)}
                                        alt={selectedBrand.marca || selectedBrand.codice}
                                        fill
                                        className="object-contain"
                                        sizes="120px"
                                    />
                                )}
                            </div>
                            <h3 className="text-2xl sm:text-3xl font-semibold text-gray-900">
                                Modelli {selectedBrand?.marca || selectedBrand?.codice}
                            </h3>
                        </div>

                        {loadingModels ? (
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                                {[...Array(8)].map((_, i) => (
                                    <div key={i} className="h-12 rounded-xl bg-gray-200 animate-pulse" />
                                ))}
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                                {models.map((model) => (
                                    <button
                                        key={model.modello}
                                        type="button"
                                        onClick={() => setSelectedModel(model.modello)}
                                        className={cn(
                                            "rounded-xl border px-3 py-3 text-sm sm:text-base font-medium text-gray-800 hover:border-primary hover:shadow transition",
                                            selectedModel === model.modello
                                                ? "border-primary ring-2 ring-primary/30 bg-primary/5"
                                                : "border-gray-300"
                                        )}
                                    >
                                        {model.modello} {model.count ? `(${model.count})` : ""}
                                    </button>
                                ))}
                                {!models.length && (
                                    <div className="col-span-full text-center text-sm text-gray-500">
                                        Nessun modello disponibile.
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="mt-8">
                            <button
                                type="button"
                                onClick={handleNavigate}
                                className="w-full bg-gradient-custom text-white font-semibold py-3.5 rounded-xl shadow hover:translate-y-[-2px] transition"
                            >
                                {selectedModel ? "Cerca" : "Vedi Tutto"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}
